import React, { useState, useRef, useEffect } from 'react';
import './AccommodationPage.css';
import API_URL from '../config';

const HOTELS = [
  {
    id: 'arcade',
    name: "Hôtel de l'Arcade",
    stars: 3,
    address: "9 rue de l'Arcade, 75008 Paris",
    distance: '7 min walk from Pavillon Cambon',
    description:
      "Located in the heart of the 8th arrondissement of Paris, steps from La Madeleine and the grand boulevards, Hôtel de l'Arcade is a charming and authentic 3-star hotel. Its comfortable rooms in a classic style, warm and peaceful atmosphere, and convivial breakfast room make it an ideal address to enjoy Paris.",
    images: [
      '/images/hotels/arcade-facade.webp',
      '/images/hotels/arcade-breakfast.webp',
      '/images/hotels/arcade-chambre.webp',
    ],
    rooms: [
      { type: 'Single Room', price: 240, capacity: 3 },
      { type: 'Classic Double Room', price: 320, capacity: 10 },
      { type: 'Duplex Room', price: 400, capacity: 2 },
      { type: 'Deluxe Room (2 pers.)', price: 460, capacity: 1 },
    ],
    priceNote: 'Excluding city tax — breakfast not included',
    deadline: '2026-04-01',
  },
  {
    id: 'triangle',
    name: "Hôtel du Triangle d'Or",
    stars: 3,
    address: '6 rue Godot de Mauroy, 75009 Paris',
    distance: '3 min walk from Pavillon Cambon',
    description:
      "Situated steps from the Opéra Garnier, Hôtel du Triangle d'Or is a contemporary 3-star hotel with a warm atmosphere. Appreciated for its comfort, peaceful ambiance and central location, it is an ideal base for staying in Paris, close to major cultural landmarks and vibrant neighbourhoods.",
    images: [
      '/images/hotels/triangle-facade.webp',
      '/images/hotels/triangle-bar.webp',
      '/images/hotels/triangle-chambre.webp',
    ],
    rooms: [
      { type: 'Double Room (single use)', price: 490, note: 'breakfast included', capacity: 30 },
    ],
    priceNote: 'Excluding city tax',
    deadline: '2026-04-01',
  },
  {
    id: 'bedford',
    name: 'Hôtel Bedford',
    stars: 4,
    address: "17 rue de l'Arcade, 75008 Paris",
    distance: '8 min walk from Pavillon Cambon',
    description:
      "Located in the 8th arrondissement of Paris, close to La Madeleine and the grand boulevards, Hôtel Bedford is an elegant and comfortable 4-star hotel. With its 50 rooms, bar and restaurant, it combines classic Parisian charm with modern comfort in the heart of a prestigious neighbourhood.",
    images: [
      '/images/hotels/bedford-facade.webp',
      '/images/hotels/bedford-lounge.webp',
      '/images/hotels/bedford-chambre.webp',
    ],
    rooms: [
      { type: 'Classic Room (19 m²)', price: 310, capacity: 15 },
      { type: 'Superior Room (22 m²)', price: 360, capacity: 20 },
      { type: 'DeLuxe Room (30 m²)', price: 430, capacity: 15 },
    ],
    priceNote: 'Excluding city tax — breakfast not included',
    deadline: '2026-03-18',
  },
];

function Stars({ count }) {
  return (
    <div className="hotel-stars" aria-label={`${count} stars`}>
      {Array.from({ length: count }).map((_, i) => (
        <span key={i} className="star">★</span>
      ))}
    </div>
  );
}

function formatDeadline(dateStr) {
  const [year, month, day] = dateStr.split('-');
  const d = new Date(year, month - 1, day);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function HotelCard({ hotel, status, availability, onReserve }) {
  const [imgIdx, setImgIdx] = useState(0);

  const prev = (e) => {
    e.stopPropagation();
    setImgIdx((i) => (i - 1 + hotel.images.length) % hotel.images.length);
  };
  const next = (e) => {
    e.stopPropagation();
    setImgIdx((i) => (i + 1) % hotel.images.length);
  };

  return (
    <div className={`hotel-card stars-${hotel.stars}`}>
      <div className="hotel-card-gallery">
        <img
          src={hotel.images[imgIdx]}
          alt={`${hotel.name} — photo ${imgIdx + 1}`}
          className="hotel-card-photo"
        />
        {hotel.images.length > 1 && (
          <>
            <button className="gallery-btn gallery-prev" onClick={prev} aria-label="Previous photo">‹</button>
            <button className="gallery-btn gallery-next" onClick={next} aria-label="Next photo">›</button>
            <div className="gallery-dots">
              {hotel.images.map((_, i) => (
                <button
                  key={i}
                  className={`gallery-dot${i === imgIdx ? ' active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setImgIdx(i); }}
                  aria-label={`Photo ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="hotel-card-info">
        <div className="hotel-card-header">
          <Stars count={hotel.stars} />
          <h3 className="hotel-card-name">{hotel.name}</h3>
          <p className="hotel-card-address">{hotel.address}</p>
          <span className="hotel-card-distance">{hotel.distance}</span>
        </div>

        <p className="hotel-card-desc">{hotel.description}</p>

        <div className="hotel-pricing">
          <h4 className="pricing-title">Rates per night</h4>
          <table className="pricing-table">
            <tbody>
              {hotel.rooms.map((room, i) => {
                const remaining = room.capacity != null
                  ? Math.max(0, room.capacity - ((availability[hotel.name] || {})[room.type] || 0))
                  : null;
                const roomFull = remaining !== null && remaining === 0;
                return (
                  <tr key={i} className={roomFull ? 'room-row-full' : ''}>
                    <td className="room-type">
                      {room.type}
                      {room.note && <span className="room-note"> — {room.note}</span>}
                      {remaining !== null && !roomFull && (
                        <span className="room-remaining">{remaining} left</span>
                      )}
                      {roomFull && <span className="room-full-badge">Full</span>}
                    </td>
                    <td className="room-price">{room.price}&nbsp;€</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="price-note">{hotel.priceNote}</p>
        </div>

        {hotel.deadline && (
          <p className="hotel-deadline">Booking deadline: {formatDeadline(hotel.deadline)}</p>
        )}

        {status === 'open' && (
          <button className="btn-reserve" onClick={() => onReserve(hotel)}>
            Book
          </button>
        )}
        {status === 'full' && (
          <span className="btn-fully-booked">Fully Booked</span>
        )}
        {status === 'deadline' && (
          <span className="btn-booking-closed">Booking Closed</span>
        )}
      </div>
    </div>
  );
}

function AccommodationPage() {
  const bookingRef = useRef(null);

  /* ── Hotel availability ── */
  const [availability, setAvailability] = useState({});

  const fetchAvailability = () => {
    fetch(`${API_URL}/api/hotel-availability`)
      .then((r) => r.json())
      .then(setAvailability)
      .catch(() => {});
  };

  useEffect(() => { fetchAvailability(); }, []);

  function getRoomRemaining(hotel, room) {
    if (room.capacity == null) return null;
    const booked = (availability[hotel.name] || {})[room.type] || 0;
    return Math.max(0, room.capacity - booked);
  }

  function getHotelStatus(hotel) {
    const today = new Date().toISOString().split('T')[0];
    if (hotel.deadline && today > hotel.deadline) return 'deadline';
    // Hotel is full only if every room with a capacity limit is full AND no unlimited rooms exist
    const hasUnlimited = hotel.rooms.some((r) => r.capacity == null);
    if (hasUnlimited) return 'open';
    const allFull = hotel.rooms.every((r) => {
      const booked = (availability[hotel.name] || {})[r.type] || 0;
      return r.capacity != null && booked >= r.capacity;
    });
    return allFull ? 'full' : 'open';
  }

  /* ── Booking form state ── */
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [emailError, setEmailError] = useState('');
  const [registration, setRegistration] = useState(null);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [bookingData, setBookingData] = useState({
    checkInDate: '',
    checkOutDate: '',
    roomType: '',
    numberOfGuests: 1,
    specialRequests: '',
  });
  const [formError, setFormError] = useState('');
  const [bookingConfirmation, setBookingConfirmation] = useState(null);

  const handleReserve = (hotel) => {
    setSelectedHotel(hotel);
    setStep(1);
    setTimeout(() => bookingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  const handleEmailCheck = async (e) => {
    e.preventDefault();
    setEmailError('');
    if (!firstName.trim() || !lastName.trim()) {
      setEmailError('Please enter your first name and last name.');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/check-registration`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      setRegistration(
        res.ok && data.registered
          ? data.registration
          : { first_name: firstName, last_name: lastName, email }
      );
    } catch {
      setRegistration({ first_name: firstName, last_name: lastName, email });
    }
    setStep(selectedHotel ? 3 : 2);
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!bookingData.checkInDate || !bookingData.checkOutDate) {
      setFormError('Please select your check-in and check-out dates.');
      return;
    }
    if (new Date(bookingData.checkOutDate) <= new Date(bookingData.checkInDate)) {
      setFormError('Check-out date must be after check-in date.');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/hotel-booking`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          firstName: registration?.first_name || firstName,
          lastName: registration?.last_name || lastName,
          hotelName: selectedHotel.name,
          checkInDate: bookingData.checkInDate,
          checkOutDate: bookingData.checkOutDate,
          roomType: bookingData.roomType,
          numberOfGuests: bookingData.numberOfGuests,
          specialRequests: bookingData.specialRequests,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        setBookingConfirmation(data.booking);
        fetchAvailability();
        setStep(4);
      } else {
        setFormError(data.error || 'Booking failed. Please try again.');
      }
    } catch {
      setFormError('Connection error. Please try again.');
    }
  };

  const resetBooking = () => {
    setStep(1);
    setEmail(''); setFirstName(''); setLastName('');
    setEmailError(''); setRegistration(null); setSelectedHotel(null);
    setBookingData({ checkInDate: '', checkOutDate: '', roomType: '', numberOfGuests: 1, specialRequests: '' });
    setFormError(''); setBookingConfirmation(null);
  };

  const hotels3 = HOTELS.filter((h) => h.stars === 3);
  const hotels4 = HOTELS.filter((h) => h.stars === 4);
  const hotels5 = HOTELS.filter((h) => h.stars === 5);


  /* ── Progress steps ── */
  const stepLabels = selectedHotel
    ? ['Your details', 'Booking details']
    : ['Your details', 'Hotel selection', 'Booking details'];
  const displayStep = selectedHotel
    ? step === 3 ? 2 : 1
    : step;

  return (
    <div className="accommodation-page">

      {/* ── Hero ── */}
      <section className="accommodation-hero">
        <div className="container">
          <h1>ACCOMMODATION</h1>
          <p>Selected hotels in Paris, steps away from the venue</p>
        </div>
      </section>

      {/* ── Hotel showcase ── */}
      <section className="section hotels-showcase">
        <div className="container">
          <div className="showcase-intro">
            <h2>Hotel Recommendations</h2>
            <p>
              We have secured special rates at selected hotels near the venue.
              All hotels are within walking distance from{' '}
              <strong>Pavillon Cambon Capucines</strong> (Paris 1st), the venue
              for the CISAC General Assembly 2026.
            </p>
          </div>

          {/* 3★ */}
          <div className="hotel-category">
            <div className="category-header">
              <span className="category-stars">★★★</span>
              <h3>3-Star Hotels</h3>
            </div>
            <div className="hotels-grid-2">
              {hotels3.map((h) => <HotelCard key={h.id} hotel={h} status={getHotelStatus(h)} availability={availability} onReserve={handleReserve} />)}
            </div>
          </div>

          {/* 4★ */}
          {hotels4.length > 0 && (
            <div className="hotel-category">
              <div className="category-header">
                <span className="category-stars">★★★★</span>
                <h3>4-Star Hotel</h3>
              </div>
              {hotels4.map((h) => <HotelCard key={h.id} hotel={h} status={getHotelStatus(h)} availability={availability} onReserve={handleReserve} />)}
            </div>
          )}

          {/* 5★ */}
          {hotels5.length > 0 && (
            <div className="hotel-category">
              <div className="category-header category-header-luxury">
                <span className="category-stars">★★★★★</span>
                <h3>5-Star Hotel</h3>
              </div>
              {hotels5.map((h) => <HotelCard key={h.id} hotel={h} status={getHotelStatus(h)} availability={availability} onReserve={handleReserve} />)}
            </div>
          )}
        </div>
      </section>

      {/* ── Booking form ── */}
      <section className="section booking-section" ref={bookingRef}>
        <div className="container">
          <h2>Book Your Hotel</h2>

          {selectedHotel && step < 4 && (
            <div className="selected-hotel-banner">
              <span className="selected-label">Selected hotel:</span>
              <strong>{selectedHotel.name}</strong>
              <button className="btn-change" onClick={() => setSelectedHotel(null)}>Change</button>
            </div>
          )}

          {step < 4 && (
            <div className="progress-steps">
              {stepLabels.map((label, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div className={`progress-line${displayStep > i + 1 ? ' completed' : ''}`} />}
                  <div className={`progress-step${displayStep >= i + 1 ? ' active' : ''}${displayStep > i + 1 ? ' completed' : ''}`}>
                    <div className="step-number">{displayStep > i + 1 ? '✓' : i + 1}</div>
                    <div className="step-label">{label}</div>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Step 1 — Details */}
          {step === 1 && (
            <div className="booking-step">
              <p className="step-desc">Enter your details to begin your booking.</p>
              <form onSubmit={handleEmailCheck} className="contact-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First name *</label>
                    <input type="text" value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="Your first name" required />
                  </div>
                  <div className="form-group">
                    <label>Last name *</label>
                    <input type="text" value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Your last name" required />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email address *</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" required />
                  <div className="booking-email-hint">
                    <span className="hint-icon">ℹ</span>
                    If you are registering for the GA, please use the same email address to link your hotel booking to your registration.
                  </div>
                </div>
                {emailError && <div className="error-message">{emailError}</div>}
                <button type="submit" className="btn btn-primary">Continue →</button>
              </form>
            </div>
          )}

          {/* Step 2 — Hotel selection (only when no hotel pre-selected) */}
          {step === 2 && !selectedHotel && (
            <div className="booking-step">
              <p className="step-desc">
                Welcome {registration?.first_name}! Choose your hotel for CISAC GA 2026.
              </p>
              <div className="hotel-select-grid">
                {HOTELS.filter((hotel) => getHotelStatus(hotel) === 'open').map((hotel) => (
                  <button
                    key={hotel.id}
                    className="hotel-select-card"
                    onClick={() => { setSelectedHotel(hotel); setStep(3); }}
                  >
                    <img src={hotel.images[0]} alt={hotel.name} />
                    <div className="hotel-select-info">
                      <div className="hotel-stars-small">
                        {Array.from({ length: hotel.stars }).map((_, i) => <span key={i}>★</span>)}
                      </div>
                      <h4>{hotel.name}</h4>
                      <p>{hotel.distance}</p>
                    </div>
                  </button>
                ))}
              </div>
              <button onClick={() => setStep(1)} className="btn btn-back">← Back</button>
            </div>
          )}

          {/* Step 3 — Booking details */}
          {step === 3 && selectedHotel && (
            <div className="booking-step">
              <form onSubmit={handleBookingSubmit} className="booking-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Check-in date *</label>
                    <input
                      type="date"
                      value={bookingData.checkInDate}
                      onChange={(e) => setBookingData({ ...bookingData, checkInDate: e.target.value })}
                      min={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Check-out date *</label>
                    <input
                      type="date"
                      value={bookingData.checkOutDate}
                      onChange={(e) => setBookingData({ ...bookingData, checkOutDate: e.target.value })}
                      min={bookingData.checkInDate || new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Room type</label>
                    <select
                      value={bookingData.roomType}
                      onChange={(e) => setBookingData({ ...bookingData, roomType: e.target.value })}
                    >
                      <option value="">Select a type</option>
                      {selectedHotel.rooms.map((r, i) => {
                        const rem = r.capacity != null
                          ? Math.max(0, r.capacity - ((availability[selectedHotel.name] || {})[r.type] || 0))
                          : null;
                        const full = rem !== null && rem === 0;
                        return (
                          <option key={i} value={r.type} disabled={full}>
                            {r.type} — {r.price}&nbsp;€/night{full ? ' (Full)' : rem !== null ? ` (${rem} left)` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Number of guests *</label>
                    <input
                      type="number"
                      value={bookingData.numberOfGuests}
                      onChange={(e) => setBookingData({ ...bookingData, numberOfGuests: parseInt(e.target.value) })}
                      min="1" max="4" required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Special requests</label>
                  <textarea
                    value={bookingData.specialRequests}
                    onChange={(e) => setBookingData({ ...bookingData, specialRequests: e.target.value })}
                    placeholder="Special needs, accessibility, preferences…"
                    rows="3"
                  />
                </div>
                {formError && <div className="error-message">{formError}</div>}
                <div className="form-actions">
                  <button type="button" onClick={() => setStep(selectedHotel ? 1 : 2)} className="btn btn-back">← Back</button>
                  <button type="submit" className="btn btn-primary">Send request</button>
                </div>
              </form>
            </div>
          )}

          {/* Step 4 — Success */}
          {step === 4 && bookingConfirmation && (
            <div className="booking-step success-step">
              <div className="success-icon">✓</div>
              <h3>Request sent successfully!</h3>
              <p className="success-message">
                A confirmation email has been sent to <strong>{email}</strong>.
                Our team will validate your booking as soon as possible.
              </p>
              <div className="booking-summary">
                <div className="summary-item">
                  <span className="summary-label">Hotel</span>
                  <span className="summary-value">{bookingConfirmation.hotel_name}</span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Check-in</span>
                  <span className="summary-value">
                    {new Date(bookingConfirmation.check_in_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                </div>
                <div className="summary-item">
                  <span className="summary-label">Check-out</span>
                  <span className="summary-value">
                    {new Date(bookingConfirmation.check_out_date).toLocaleDateString('en-GB', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                  </span>
                </div>
              </div>
              <button onClick={resetBooking} className="btn btn-primary">New booking</button>
            </div>
          )}
        </div>
      </section>

      {/* ── Contact ── */}
      <section className="section contact-section">
        <div className="container">
          <h2>NEED ASSISTANCE?</h2>
          <p>For any accommodation-related questions, please contact our team:</p>
          <div className="contact-card">
            <p>Registration &amp; Accommodation</p>
            <p>Email: <a href="mailto:2026ga@cisac.org">2026ga@cisac.org</a></p>
          </div>
        </div>
      </section>

    </div>
  );
}

export default AccommodationPage;
